import { EventAggregator } from 'aurelia-event-aggregator';
import {
	autoinject,
	bindingMode,
	computedFrom,
	customElement,
	PLATFORM,
	useView,
} from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-templating';

import {
	AcuritySearchApi,
	AcuritySearchApiOptions,
} from '~/api/acurity-search-api';
import { AutocompleteColumnHeader } from '~/components/field/common/autocomplete/autocomplete-react';
import { Autocomplete } from '~/components/field/common/autocomplete/autocomplete';
import { Field } from '~/components/field/common/field';
import {
	FilterBy,
	SearchDefinition,
	SearchDefinitionMap,
	SearchFieldModel,
	SearchFilter,
	SearchType,
} from '~/components/field/common/search-definition';
import { Form } from '~/components/form/form';
import { CurrencyFormatValueConverter } from '~/converters/currency-format';
import { DateFormatValueConverter } from '~/converters/date-format';
import {
	commonFieldProperties,
	registerComponent,
} from '~/forms/customisation-editor/property-meta';
import { FieldModel, Modifiable } from '~/models/form-model';
import { FormModel } from '~/models/form-model';
import { BLANK_MEMBER } from '~/models/membership';
import {
	AcurityFieldUtils,
	FieldNameParamType,
} from '~/utils/acurity-field-utils';
import { Converters } from '~/utils/converters';
import { Logger } from '~/utils/logger';
import { StringUtils } from '~/utils/string-utils';
import { LockUntilReady } from '~/utils/utils';
import { ValidateCustomElementBindingBehaviorOptions } from '~/validation/validate-custom-element-binding-behavior';

enum SearchStatus {
	IDLE = 'IDLE',
	SEARCHING = 'SEARCHING',
	DONE = 'DONE',
}

const componentTagName = 'a-search';

registerComponent(componentTagName, {
	name: 'Search',
	requirePath: PLATFORM.moduleName('components/field/common/search'),
	props: commonFieldProperties,
	isSelectable: true,
});

@autoinject
@customElement(componentTagName)
@useView(PLATFORM.moduleName('./search.html'))
export class Search extends Field {
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'searchTypeChanged',
	})
	private searchType: SearchType;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private columnName: string = '';

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private columnHeaders?: AutocompleteColumnHeader[];

	/**
	 * formModel is required when using "filterBy" with this search field.
	 * The formModel is inferred from the "Form" context if possible but for cases where
	 * the search field cannot infer the formModel, you can pass it in directly.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private formModel?: FormModel;

	/**
	 * Call function callback when an item is selected.
	 *
	 * NOTE:
	 * Do not re-order this after 'items' or it will not be set when "itemsChanged" fires.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private onSelect?: (
		record: { [columnName: string]: any },
		value: string,
		fieldModel?: FieldModel,
	) => void;

	@bindable({
		defaultBindingMode: bindingMode.oneWay,
		changeHandler: 'itemsChanged',
	})
	private items?: any[];

	/**
	 * This will filter the autocomplete results by the value of
	 * another form field.
	 */
	private filterBy?: string[] | FilterBy[];

	/**
	 * If this is true, the first item in the provided "items" list
	 * will be forced selected.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private selectFirst?: boolean;

	/**
	 * Add additional filters to be used with the request.
	 */
	private additionalFilters?: SearchFilter[];

	/**
	 * Automatically display results on focus if there is no value provided. If field has prefetched
	 * search items, and you want results to automatically display, remember to bind this to true.
	 */
	@bindable({
		defaultBindingMode: bindingMode.oneWay,
	})
	private displayResultsOnFocus: boolean = false;

	private keepValueWhenFilterByChanges: boolean = false;

	private searchDefinition: SearchDefinition;

	private lockUntilSearchFinished: LockUntilReady | undefined;

	private fieldTypes: readonly FieldNameParamType[];

	/**
	 * This is used by the view.
	 */
	private validateCustomElementBindingBehaviorOptions: ValidateCustomElementBindingBehaviorOptions =
		{
			elementSelector: '.a-autocomplete__input',
			onBlurValidationDelayerProvider: () => {
				return this.lockUntilSearchFinished
					? this.lockUntilSearchFinished
					: Promise.resolve();
			},
		};

	constructor(
		public element: Element,
		// NOTE: Jake: 2019-12-16
		// Im not sure this "parentForm" injection is an ideal way to allow search fields
		// to have "formModel" / "filterBy" access. I'd rather users explictily feed in
		// "form-model" as a bindable.
		//
		// To achieve this, we'd need to update customisations, so that it auto-applies
		// "form-model.bind" to the data before processing it.
		//
		// Reason we should do this is that React wont support this kind of feature, and
		// so we need to get away from it, even if we only do that effort only once we switch
		// to React.
		protected parentForm: Form,
		readonly eventAggregator: EventAggregator,
		private acuritySearchApi: AcuritySearchApi,
		protected readonly i18n: I18N,
	) {
		super(Field.TYPE.SEARCH, element, parentForm, eventAggregator);

		// NOTE: Jake: 2018-01-02 - WEBUI-1148
		// Temporarily disabled as "field.ts" will now transform
		// string dataType values to '' if the value given is undefined.
		// This patch should hopefully no longer be necessary and can be removed
		// within a month or so.
		// if (this.value === undefined) {
		// Set default value to '' but only for "search"
		// this.value = '';
		// }
	}

	@computedFrom(
		'items',
		'items.length',
		'fieldModel',
		'fieldModel.codeList',
		'searchDefinition',
	)
	get remotePromise(): AcuritySearchApi | undefined {
		// If there is no searchDefinition, we don't remote search.
		if (!this.searchDefinition) {
			return undefined;
		}
		// Catch cases where we have a searchDefinition *and* a local list of
		// data. We want to disable using remote searching if we have a local list provided.
		if (this.items !== null && this.items !== undefined) {
			return undefined;
		}
		if (
			this.fieldModel &&
			this.fieldModel.codeList !== null &&
			this.fieldModel.codeList !== undefined
		) {
			return undefined;
		}
		return this.acuritySearchApi;
	}

	protected itemsChanged(
		items: any[] | undefined,
		oldItems: any[] | undefined,
	): void {
		// NOTE: Jake: 2018-12-06 - WEBUI-1204
		// Need "selectFirst" for the client-summary page. Policy Number uses it.
		if (
			this.selectFirst &&
			(!oldItems || oldItems.length === 0) &&
			items &&
			items.length > 0
		) {
			const record = items[0];
			const columnValue = record[this.columnName];
			if (columnValue === undefined) {
				throw new Error(
					'select-first: Expected record to have column: "' +
						this.columnName +
						'" but it\'s missing.',
				);
			}
			this.value = record[this.columnName];
			this.triggerSelectedItemChange(record);
		}
	}

	protected fieldModelChanged(newFieldModel: SearchFieldModel): void {
		if (this.fieldModel) {
			// NOTE: Jake: 2019-06-21 - WEBUI-2157 / WEBUI-2098
			// Disable inputMask behaviour for <a-search> field.
			// The inputMask library conflicts with the expected behaviour that you must select
			// a valid value from the list of items or else it clears the value.
			// We don't want to remove inputMasks from *.json files as that would stop
			// values from being validated by the backend correctly.
			//
			// NOTE: Jake: 2019-07-18 - WEBUI-2278
			// We also must apply this *before* super.fieldModelChanged() as that's when
			// the inputMask is applied to the field. This ensures that if the field value
			// is changed and saved, that the inputMask won't suddenly be applied.
			this.fieldModel.inputMask = '';
		}
		super.fieldModelChanged(newFieldModel);
		if (newFieldModel) {
			const searchFieldModel: SearchFieldModel = this.fieldModel;
			if (searchFieldModel.searchType) {
				// NOTE(Jae): 2020-03-19 - WEBUI-3098
				// If a user explicitly defines this field to have a searchType in a forms
				// frontend code, then that takes precedence and we want to *ignore* the codeList
				// logic below.
				this.searchType = searchFieldModel.searchType;
			} else if (
				(!this.items || this.items.length === 0) &&
				newFieldModel.codeList &&
				newFieldModel.codeList.length > 0
			) {
				// NOTE(Jae): 2020-03-19 - WEBUI-3098
				// If a field-model has a codeList defined on the field, then we will allow the search
				// field to work like a select/dropdown field.
				this.columnHeaders = [
					{
						columnName: 'code',
						display: 'Code',
						// NOTE(Lucas): 25/02/2020 - WEBUI-2308
						// Any character in a code should be searched for.
						tokenizer: (str) => (str ? [str] : []),
					},
					{ columnName: 'description', display: 'Description' },
				];
				this.columnName = 'code';
				this.displayResultsOnFocus = true;
			} else {
				// NOTE(Jae): 2020-03-19
				// Final fallback on inferring the search type based on the field name
				this.searchType = this.calculateSearchType(
					this.fieldModel.name,
				);
			}
			if (searchFieldModel.additionalFilters) {
				this.additionalFilters = searchFieldModel.additionalFilters;
			}
			if (searchFieldModel.displayResultsOnFocus) {
				this.displayResultsOnFocus =
					searchFieldModel.displayResultsOnFocus;
			}
			if (searchFieldModel.keepValueWhenFilterByChanges) {
				this.keepValueWhenFilterByChanges =
					searchFieldModel.keepValueWhenFilterByChanges;
			}
			if (searchFieldModel.filterBy) {
				this.filterBy = searchFieldModel.filterBy;

				if (this.filterBy.length > 0) {
					const formModel = this.getFormModel();
					if (formModel) {
						// clear existing value in this search field if one of dependent fields in filterBy is modified
						for (const filterByObj of this.filterBy) {
							let filterByCol: string;
							if (typeof filterByObj === 'object') {
								filterByCol = filterByObj.col;
							} else {
								filterByCol = filterByObj;
							}
							const fieldModel = formModel.fields[filterByCol];
							if (fieldModel) {
								this.eventSubscriptions.push(
									this.bindingEngine
										.propertyObserver(fieldModel, 'value')
										.subscribe(() =>
											this.onFilterByValueChanged(
												filterByCol,
											),
										),
								);
							}
						}
					}
				}
			}
			if (searchFieldModel.fieldTypes) {
				this.fieldTypes = searchFieldModel.fieldTypes;
			}
			// NOTE(Jae): 2020-03-10 - WEBUI-3165
			// Removing this as it was meant to help capture areas where search-type was being
			// used on customisations. Since it's mostly fixed, we can remove this in 1 month or so.
			// if (DEVELOPMENT) {
			// 	if (this.searchType === fieldModelSearchType) {
			// 		Logger.warning('<a-search> has "search-type" set, but it\'s able to infer that exact same search-type from the fieldModel. Should "search-type" attribute be removed?', '<a-search search-type="' + fieldModelSearchType + '">');
			// 	}
			// }
		}
	}

	/**
	 * Special handler for change description to '' for field doesn't have field model and value is blank
	 * eg. QAS search
	 * @override
	 */
	protected valueChanged(newValue, oldValue) {
		if (!this.fieldModel || StringUtils.isBlank(newValue)) {
			this.description = '';
		}
		super.valueChanged(newValue, oldValue);
	}

	private onFilterByValueChanged(filterByCol: string): void {
		const formModel = this.getFormModel();
		if (!formModel) {
			return;
		}
		const thisFieldModel = formModel.fields[this.fieldModel.name];
		if (!thisFieldModel || thisFieldModel.modifiable === Modifiable.FALSE) {
			// NOTE(Jae): 2020-03-03 - WEBUI-3021
			// Tested "defaultValue" function and noticed search fields still
			// reset values even if their Modifiable.FALSE.
			return;
		}
		if (this.keepValueWhenFilterByChanges) {
			if (this.displayResultsOnFocus && this.refInputElem) {
				Autocomplete.updateTypeAheadDataSets(this.refInputElem);
			}
			return;
		}
		// NOTE(York): WEBUI-3183
		// If member is set to '******', do not clear it since '******' is valid for any fund.
		if (
			thisFieldModel.name.endsWith('z_Member') &&
			filterByCol.endsWith('z_Fund') &&
			thisFieldModel.value === BLANK_MEMBER
		) {
			return;
		}
		thisFieldModel.value = '';
		this.description = '';

		// NOTE(York): WEBUI-3191
		// If displayResultsOnFocus is true and this field value is cleared, should update the
		// typeahead dataset since it may change due to the change of its dependent filterBy.
		if (this.displayResultsOnFocus && this.refInputElem) {
			Autocomplete.updateTypeAheadDataSets(this.refInputElem);
		}
	}

	private searchTypeChanged(newSearchType, oldSearchType): void {
		if (!newSearchType) {
			// Ignore if search type is blank
			return;
		}
		const newDefinition = SearchDefinitionMap[newSearchType];
		if (newDefinition === undefined) {
			throw new Error(
				'Cannot find search type in SearchDefinitionMap: ' +
					newSearchType,
			);
		}
		if (this.searchDefinition === newDefinition) {
			return;
		}
		this.searchDefinition = newDefinition;
		this.getDescriptionIfPreselectValue();
		this.columnHeaders = [];
		this.columnName = this.searchDefinition.rowIdKey;
		for (const columnDef of this.searchDefinition.columnDefinition) {
			if (!columnDef.name || !columnDef.label) {
				throw new Error(
					'search: Missing "name" or "label" property on search definition type: ' +
						newSearchType,
				);
			}
			let formatter;
			{
				// getFormatter
				if (columnDef.dataType && columnDef.dataType.name) {
					switch (columnDef.dataType.name) {
						case 'Date':
							formatter = this.formatDate;
							break;
						case 'Currency':
							formatter = (val) =>
								Converters.currencyFormat(val, 2);
							break;

						default:
							const exhaustiveCheck: never =
								columnDef.dataType.name;
							throw new Error(
								'Unhandled columnDef.dataType.name type: ' +
									columnDef.dataType.name,
							);
					}
				}
				switch (newSearchType) {
					case 'PAYROLL':
						// NOTE(Jake): 2018-12-03 - WEBUI-1204
						// I tested this by forcing this codepath to be true temporarily. I don't
						// think this logic is used anymore as my searches seem to indicate that ABN
						// fields use <a-text> not <a-search>.
						if (columnDef.name === 'COz_Aust_Company_No') {
							formatter = this.formatABN;
						}
						break;
				}
			}
			const row: AutocompleteColumnHeader = {
				columnName: columnDef.name,
				display: columnDef.label,
				hidden: columnDef.hidden,
				formatter,
				render: columnDef.render,
			};
			this.columnHeaders.push(row);
		}
	}

	@computedFrom(
		'searchDefinition',
		'filterBy',
		'parentForm',
		'parentForm.formModel',
		'formModel',
		'additionalFilters',
	)
	get remoteOptions(): AcuritySearchApiOptions {
		return {
			name:
				this.fieldModel && this.fieldModel.name
					? this.fieldModel.name
					: this.fieldId,
			searchDefinition: this.searchDefinition,
			filterBy: this.filterBy,
			formModel: this.getFormModel(),
			additionalFilters: this.additionalFilters,
		};
	}

	/**
	 * Calculates the search type based on the fieldName
	 * @param fieldName {string} Field name from the fieldModel
	 */
	private calculateSearchType(fieldName: string): SearchType {
		const searchTypeRegexp = /^\w{2}[yzl]_(.+)$/g;
		const match = searchTypeRegexp.exec(fieldName);
		if (match && match.length > 1 && match[1]) {
			const searchTypeName = match[1].toUpperCase();
			const searchType = SearchType[searchTypeName];
			if (searchType !== undefined) {
				return searchType;
			} else if (
				searchTypeName === 'BROKER' ||
				searchTypeName === 'BROKER_CODE'
			) {
				// we can imply that all '_Broker' and '_Broker_Code' fields are this SearchType
				return SearchType.ADVISER;
			}
		}
		return null;
	}

	/**
	 * Format date field types
	 */
	private readonly formatDate = (value: string): string => {
		// NOTE(Jake): 2018-12-03 - WEBUI-1204
		// I tested this here on the "Category*" field (CTz_Category)
		// http://localhost:9000/#/classic-form/category-description
		return DateFormatValueConverter.toLocaleDate(value);
	};

	/**
	 * Format ABN string
	 */
	private readonly formatABN = (value: string): string => {
		value = value.toString().trim().replace(/[ -]/g, '');
		let abn = value.substring(0, 2);
		if (value.length > 2) {
			abn += ' ' + value.substring(2, 5);
		}
		if (value.length > 5) {
			abn += '-' + value.substring(5, 8);
		}
		if (value.length > 8) {
			abn += '-' + value.substring(8, value.length);
		}
		return abn;
	};

	private readonly triggerSelectedItemChange = (
		record: { [columnName: string]: any } | undefined,
	): void => {
		if (record === undefined) {
			if (this.onSelect) {
				this.onSelect(undefined, '', this.fieldModel);
			}
			this.description = '';
			return;
		}
		const columnValue = record[this.columnName];
		if (columnValue === undefined) {
			throw new Error(
				'select-first: Expected record to have column: "' +
					this.columnName +
					'" but it\'s missing.',
			);
		}
		if (this.searchDefinition) {
			this.description = '';
			const descArray: string[] =
				this.searchDefinition.descriptionDefinition.map((def) =>
					record[def] ? record[def] : '',
				);

			this.description =
				AcurityFieldUtils.descriptionFromArray(descArray);
		}

		if (this.onSelect) {
			this.onSelect(record, columnValue, this.fieldModel);
		}
	};

	private readonly onSelectAutocomplete = (
		record: { [columnName: string]: any } | undefined,
	): void => {
		this.triggerSelectedItemChange(record);
	};

	private readonly onValidationError = (hasError: string): void => {
		if (hasError) {
			this.validationMessage = this.i18n.tr('search.select-valid-option');
		} else {
			this.validationMessage = '';
		}
	};

	protected shouldSkipEndFieldInBlur(
		relatedTarget: HTMLElement | undefined,
	): boolean {
		// NOTE(Jae): 2020-03-11 - WEBUI-3165
		// We want to make sure that if a user:
		// - Selects an item from an <a-autocomplete> / <a-search> field
		// - Then clicks [X] (clear button)
		// - That we do *not* fire an end field event.
		// This resolves a bug where:
		// - Clicking [X] would fire the blur event (firing end-field API call), then the click event for the clear.
		// - This meant the value sent to the end-field API would *not* be empty
		// - So after clearing the value, a short time after, the value would come back immediately.
		if (
			relatedTarget &&
			relatedTarget.hasAttribute('data-skipendfieldonblur')
		) {
			return true;
		}
		return false;
	}

	/*
	 Called by Aurelia
	 */
	private readonly onSearchStarted = (): void => {
		if (!this.lockUntilSearchFinished) {
			this.lockUntilSearchFinished = new LockUntilReady();
		}
	};

	/*
	 Called by Aurelia
	 */
	private readonly onSearchFinished = (): void => {
		if (this.lockUntilSearchFinished) {
			this.lockUntilSearchFinished.triggerOnce();
			this.lockUntilSearchFinished = undefined;
		}
	};

	/**
	 * @override
	 */
	protected onBlurMaybeTriggerEndFieldEvent(e: FocusEvent) {
		// NOTE(Lucas): 2020-04-09 - WEBUI-3268
		// It is not guaranteed if 'blur' or 'change' is fired first (depends on the browser). We need
		// 'blur' to be handled after 'change' since 'change' leads to a change of the value property
		// asynchronously and an end-field event must be fired after the value property change, thus the end-field
		// handler needs to know if we are waiting for the value property to be updated in order to delay firing the
		// end-field event.
		// This window.requestAnimationFrame forces 'blur' to be handled after 'change'.
		window.requestAnimationFrame(() =>
			super.onBlurMaybeTriggerEndFieldEvent(e),
		);
	}

	/**
	 * @override
	 */
	protected triggerEndFieldEvent(
		relatedTarget: HTMLElement | undefined,
	): void {
		if (this.lockUntilSearchFinished) {
			// NOTE(Lucas): 2020-04-09 - WEBUI-3268
			// Waiting until the search has finished until triggering the end-field event is intended to guarantee that
			// that event will be handled after the value property and fieldModel.value have been set.
			this.lockUntilSearchFinished.then((_) => {
				super.triggerEndFieldEvent(relatedTarget);
			});
		} else {
			super.triggerEndFieldEvent(relatedTarget);
		}
	}

	@computedFrom('items', 'fieldModel.codeList', 'columnName')
	private get searchItems(): any[] {
		// NOTE: Jake: 2019-12-20 - WEBUI-2856
		// columnName is needed to get a value from a column in the
		// searchItems list, so we disallow getting the list until we
		// have a columnName populated. This prevents errors, at least on
		// the asset-transaction-list page
		if (!this.columnName) {
			return [];
		}
		if (
			this.fieldModel &&
			this.fieldModel.codeList !== undefined &&
			this.fieldModel.codeList !== null
		) {
			return this.fieldModel.codeList;
		}
		return this.items;
	}

	private getFormModel(): FormModel {
		return this.formModel
			? this.formModel
			: this.parentForm
			? this.parentForm.formModel
			: undefined;
	}

	/**
	 * Search field can have preSelect value from field model before starting search query when focused. If search field model contains
	 * value but has no description (normall from codeList), additional request should be called to get the description. i,e.
	 * MemberBenefitQuotesDialog has a search field `BSz_Batch_Code` get value from preInsert but has no description.
	 * This method now is only called when `this.searchType` changed.
	 */
	private getDescriptionIfPreselectValue() {
		this.whenBlankSearchForAndSetDescription(this.fieldModel);
	}

	/**
	 * If the given field model does not contain a description, do a search for it.
	 * @param fieldModel
	 * @override
	 */
	protected calculateAndSetDescriptionFromFieldModel(
		fieldModel: FieldModel,
	): void {
		super.calculateAndSetDescriptionFromFieldModel(fieldModel);
		this.whenBlankSearchForAndSetDescription(fieldModel);
	}

	private whenBlankSearchForAndSetDescription(fieldModel: FieldModel): void {
		if (
			this.searchDefinition &&
			this.searchDefinition.descriptionDefinition?.length !== 0 &&
			!this.description &&
			fieldModel &&
			fieldModel.value
		) {
			// We need to set additionalFilters here because this may fire before fieldModelChanged()
			const searchFilters: SearchFilter[] = (
				this.fieldModel as SearchFieldModel
			).additionalFilters;
			if (searchFilters) {
				this.additionalFilters = this.additionalFilters
					? [...this.additionalFilters, ...searchFilters]
					: searchFilters;
			}
			this.acuritySearchApi
				.getSearchDescription(
					fieldModel.value,
					this.searchDefinition,
					this.additionalFilters,
				)
				.then((description) => (this.description = description));
		}
	}
}
